import { sbi_nl } from '@/utils/maps/sbi-nl.js';
import { dateOptionToEpoch } from '@/utils/dates';

export function mapCurrentDataset(dataset = null) {
    if(dataset && dataset.sbi) {
        dataset.sbi.includes = Array.isArray(dataset.sbi.includes) && dataset.sbi.includes.length > 0 && typeof dataset.sbi.includes[0] === 'string' 
            ? dataset.sbi.includes.map(sbi => ({ value: sbi, text: findSBIName(sbi) })) 
            : (dataset.sbi.includes || []);
        dataset.sbi.excludes = Array.isArray(dataset.sbi.excludes) && dataset.sbi.excludes.length > 0 && typeof dataset.sbi.excludes[0] === 'string' 
            ? dataset.sbi.excludes.map(sbi => ({ value: sbi, text: findSBIName(sbi) })) 
            : (dataset.sbi.excludes || []);
    }

    if(dataset && dataset.text && (!dataset.text.custom || dataset.text.custom === "")) {
        dataset.text.includes = Array.isArray(dataset.text.includes) && dataset.text.includes.length > 0 && typeof dataset.text.includes[0] === 'string' 
            ? dataset.text.includes.map(text => ({ value: text, text })) 
            : (dataset.text.includes || []);
        dataset.text.excludes = Array.isArray(dataset.text.excludes) && dataset.text.excludes.length > 0 && typeof dataset.text.excludes[0] === 'string' 
            ? dataset.text.excludes.map(text => ({ value: text, text })) 
            : (dataset.text.excludes || []);
        dataset.text.required = Array.isArray(dataset.text.required) && dataset.text.required.length > 0 && typeof dataset.text.required[0] === 'string'
            ? dataset.text.required.map(text => ({ value: text, text })) 
            : (dataset.text.required || []);
    }

    if(dataset && dataset.relations) {
        dataset.relations.includes = Array.isArray(dataset.relations.includes) && dataset.relations.includes.length > 0 && typeof dataset.relations.includes[0] === 'string' 
            ? dataset.relations.includes.map(text => ({ value: text, text })) 
            : (dataset.relations.includes || []);
        dataset.relations.excludes = Array.isArray(dataset.relations.excludes) && dataset.relations.excludes.length > 0 && typeof dataset.relations.excludes[0] === 'string' 
            ? dataset.relations.excludes.map(text => ({ value: text, text })) 
            : (dataset.relations.excludes || []);
    }

    return dataset;
}

export function mapLegacyDataset(dataset = null) {
    let defaultDataset = {
        text: {
            includes: [],
            excludes: [],
            required: [],
            custom: dataset?.text || ''
        },
        socials: dataset?.socials || [],
        orgtype: dataset?.orgtypes || [],
        location: {
            province: dataset?.location?.provinces?.length > 0 ? dataset.location.provinces : [],
            city: dataset?.location?.city || '',
            radius: dataset?.location?.distance || null,
        },
        apps: {
            includes: [],
            excludes: [],
            required: [],
        },
        sbi: {
            includes: dataset?.sbi?.length > 0 ? dataset.sbi.map(sbi => ({value: sbi, text: findSBIName(sbi)})) : [],
            excludes: []
        },
        company_size: {
            employees: {
                min: dataset?.employees?.min || null,
                max: dataset?.employees?.max || null
            },
            revenue: {
                min: null,
                max: null
            },
        },
        founded: {
            min: dataset?.founded?.min || null,
            max: dataset?.founded?.max || null
        },
        extra: {
            phone_required: dataset?.extra?.phone || false,
            email_required: dataset?.extra?.mail || false,
            website_required: dataset?.extra?.website || false,
            exclude_branches: !dataset?.extra?.branches || true,
            exclude_holdings: dataset?.extra?.holdings || true,
            exclude_bankruptcies: dataset?.extra?.bankruptcy || true,
            coc_required: true,
            address_required: false,
            vat_required: false,
            webshop_required: false
        }
    };

    if(dataset?.apps?.length > 0 || dataset?.cms?.length > 0 || dataset?.shops?.length > 0) {
        defaultDataset.apps.required = [
            ...(dataset.apps?.length ? dataset.apps.map(app => ({value: app, text: app.charAt(0).toUpperCase() + app.slice(1).toLowerCase()})) : []),
            ...(dataset.cms?.length ? dataset.cms.map(cms => ({value: cms, text: cms.charAt(0).toUpperCase() + cms.slice(1).toLowerCase()})) : []),
            ...(dataset.shops?.length ? dataset.shops.map(shop => ({value: shop, text: shop.charAt(0).toUpperCase() + shop.slice(1).toLowerCase()})) : [])
        ];
    }

    return defaultDataset;
}

export function findSBIName(sbiCode) {
    if (!sbiCode) return '';
    
    function searchInItems(items) {
        for (const item of items) {
            if (item.value === sbiCode) {
                return item.text;
            }
            
            if (item.children && item.children.length > 0) {
                const found = searchInItems(item.children);
                if (found) return found;
            }
        }
        return null;
    }
    
    const found = searchInItems(sbi_nl);
    return found || sbiCode;
}

export const defaultCompanyQueryOptions = {
    country: 'NL',
    text: {
        includes: [],
        excludes: [],
        required: [],
        custom: ''
    },
    socials: [],
    orgtype: [],
    location: {
        province: [],
        city: '',
        radius: null,
    },
    apps: {
        includes: [],
        excludes: [],
        required: []
    },
    relations: {
        includes: [],
        excludes: []
    },
    sbi: {
        includes: [],
        excludes: []
    },
    company_size: {
        employees: {
            min: null,
            max: null
        },
        revenue: {
            min: null,
            max: null
        },
    },
    founded: {
        min: null,
        max: null
    },
    bag: {
        gebruiksdoel: [],
        energielabel: [],
        bouwjaar: {
            min: null,
            max: null
        },
        oppervlakte: {
            min: null,
            max: null
        },
        grondoppervlakte: {
            min: null,
            max: null
        }
    },
    extra: {
        coc_required: true,
        address_required: false,
        phone_required: false,
        email_required: false,
        website_required: false,
        vat_required: false,
        webshop_required: false,
        exclude_branches: true,
        exclude_holdings: true,
        exclude_bankruptcies: true,
    }
};

export function generateCompanyQueryParams(options, includeExtra = true) {
    const processed = {
        country: options.country || 'NL'
    };

    if (options.text) {
        if (options.text.includes && options.text.includes.length > 0) {
            processed['OR_text'] = options.text.includes.map(item => {
                // return item.value.includes(' ') ? `"${item.value}"` : item.value;
                return item.value;
            });
        }

        if (options.text.required && options.text.required.length > 0) {
            processed['AND_text'] = options.text.required.map(item => {
                // return item.value.includes(' ') ? `"${item.value}"` : item.value;
                return item.value.includes(' ') ? `"${item.value}"` : item.value;
            });
        }

        if (options.text.excludes && options.text.excludes.length > 0) {
            processed['NOT_text'] = options.text.excludes.map(item => {
                // return item.value.includes(' ') ? `"${item.value}"` : item.value;
                return item.value.includes(' ') ? `"${item.value}"` : item.value;
            });
        }

        if (options.text.custom && options.text.custom.trim()) {
            processed.text = options.text.custom.trim();
        }
    }
    
    if (options.orgtype && options.orgtype.length > 0) {
        processed.orgtype = options.orgtype;
    }

    if (options.socials && options.socials.length > 0) {
        processed.social_exists = options.socials;
    }
    
    if (options.location) {
        if (options.location.province && options.location.province.length > 0) {
            processed.province = options.location.province.join(',');
        } else if (options.location.city && options.location.city.trim()) {
            if (options.location.radius) {
                processed.location = options.location.city.trim();
                processed.km = options.location.radius;
            } else {
                processed.city = options.location.city.trim();
            }
        }
    }
    
    if (options.apps) {
        if (options.apps.includes && options.apps.includes.length > 0) {
            processed['OR_apps'] = options.apps.includes.map(item => item.value);
        }

        if (options.apps.required && options.apps.required.length > 0) {
            processed['AND_apps'] = options.apps.required.map(item => item.value);
        }

        if (options.apps.excludes && options.apps.excludes.length > 0) {
            processed['NOT_apps'] = options.apps.excludes.map(item => item.value);
        }
    }
    
    if (options.relations) {
        if (options.relations.includes && options.relations.includes.length > 0) {
            processed['OR_relation'] = options.relations.includes.map(item => item.value);
        }

        if (options.relations.excludes && options.relations.excludes.length > 0) {
            processed['NOT_relation'] = options.relations.excludes.map(item => item.value);
        }
    }
    
    if (options.sbi) {
        if (options.sbi.includes && options.sbi.includes.length > 0) {
            processed['OR_sbi'] = options.sbi.includes.map(item => item.value);
        }

        if (options.sbi.excludes && options.sbi.excludes.length > 0) {
            processed['NOT_sbi'] = options.sbi.excludes.map(item => item.value);
        }
    }
    
    if (options.company_size) {
        if (options.company_size.employees) {
            const { min, max } = options.company_size.employees;
            if (min !== null || max !== null) {
                processed.employees = `${min || ''}:${max || ''}`;
            }
        }
        
        if (options.company_size.revenue) {
            const { min, max } = options.company_size.revenue;
            if (min !== null || max !== null) {
                processed.revenue = `${min || ''}:${max || ''}`;
            }
        }
    }
    
    if (options.founded) {
        const { min, max } = options.founded;
        if (min !== null || max !== null) {
            processed.founded = `${min || ''}:${max || ''}`;
        }
    }

    if (options.bag) {
        if (options.bag.gebruiksdoel && options.bag.gebruiksdoel.length > 0) {
            processed['bag_gebruiksdoel'] = options.bag.gebruiksdoel;
        }

        if (options.bag.energielabel && options.bag.energielabel.length > 0) {
            processed['bag_energielabel'] = options.bag.energielabel;
        }

        if (options.bag.bouwjaar) {
            const { min, max } = options.bag.bouwjaar;
            if (min !== null || max !== null) {
                processed['bag_bouwjaar'] = `${min || ''}:${max || ''}`;
            }
        }

        if (options.bag.oppervlakte) {
            const { min, max } = options.bag.oppervlakte;
            if (min !== null || max !== null) {
                processed['bag_oppervlakte'] = `${min || ''}:${max || ''}`;
            }
        }

        if (options.bag.grondoppervlakte) {
            const { min, max } = options.bag.grondoppervlakte;
            if (min !== null || max !== null) {
                processed['bag_grondoppervlakte'] = `${min || ''}:${max || ''}`;
            }
        }
    }

    if (includeExtra && options.extra) {
        const dataExistsParams = [];
        if (options.extra.coc_required) dataExistsParams.push('register_id');
        if (options.extra.address_required) dataExistsParams.push('address');
        if (options.extra.phone_required) dataExistsParams.push('phone');
        if (options.extra.email_required) dataExistsParams.push('email');
        if (options.extra.website_required) dataExistsParams.push('url');
        if (options.extra.vat_required) dataExistsParams.push('vat');
        
        if (dataExistsParams.length > 0) {
            processed.data_exists = dataExistsParams;
        }

        if (options.extra.webshop_required) {
            processed.apps_ecommerce = '*';
        }
        
        if (options.extra.exclude_branches) {
            processed.office_type = 'Hoofdvestiging,Rechtspersoon';
        }

        if (options.extra.exclude_holdings) {
            if(processed['data_exists']) {
                processed['data_exists'].push('no_holding');
            } else {
                processed['data_exists'] = ['no_holding'];
            }
        }

        if (options.extra.exclude_bankruptcies) {
            if(processed['-data_exists']) {
                processed['-data_exists'].push('legal_status');
            } else {
                processed['-data_exists'] = ['legal_status'];
            }
        }
    } else {
        processed.office_type = 'Hoofdvestiging,Rechtspersoon';
    }

    Object.keys(processed).forEach(key => {
        if (processed[key] === undefined || 
            processed[key] === null || 
            processed[key] === '' ||
            (Array.isArray(processed[key]) && processed[key].length === 0) ||
            (typeof processed[key] === 'object' && Object.keys(processed[key]).length === 0)) {
            delete processed[key];
        }
    });
    
    return processed;
}

export function generateProspectQueryParams(options) {
    const createdDate = dateOptionToEpoch(options.created);
    const updatedDate = dateOptionToEpoch(options.updated);

    return {
        ...(options.search && { text: options.search }),
        ...(options.sort && !options.sort.includes('-') && !options.sort.includes('default') && { sort_by: options.sort }),
        ...(options.sort && options.sort.includes('-') && { sort_by: options.sort.split('-')[0] }),
        ...(options.sort && options.sort.includes('-') && { sort_order: options.sort.split('-')[1] }),
        ...(options.label && options.label != 'all' && { label: options.label }),
        ...(createdDate && { from_inserted_date: createdDate }),
        ...(updatedDate && { from_changed_time: updatedDate }),
        ...(options.tags.length > 0 && { tags: options.tags.join(',') }),
        ...(options.lists.length > 0 && { lists: options.lists.join(',') }),
        ...(options.owner && options.owner != 'all' && { owner: options.owner }),
        ...(options.contacts && options.contacts != 'all' && { total_contacts: options.contacts }),
        ...(options.visits && options.visits != 'all' && { total_visits: options.visits })
    };
}